// Layout.tsx
import { Outlet } from "react-router-dom";

// Import all your Catalyst UI parts:
import {
  ArrowRightEndOnRectangleIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowUpTrayIcon,
  ChevronUpIcon,
  Cog8ToothIcon,
  UserIcon,
} from "@heroicons/react/16/solid";
import { EnvelopeIcon, HomeIcon, QuestionMarkCircleIcon, TableCellsIcon } from "@heroicons/react/20/solid";
import { Dropdown, DropdownButton, DropdownDivider, DropdownItem, DropdownLabel, DropdownMenu } from "./components/dropdown";
import { Link } from "./components/link";
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from "./components/navbar";
import { Sidebar, SidebarBody, SidebarFooter, SidebarHeader, SidebarItem, SidebarLabel, SidebarSection, SidebarSpacer } from "./components/sidebar";
import { SidebarLayout } from "./components/sidebar-layout";

import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import { useAuth } from "./context/useAuth";

// Example placeholder images
import skogforskLogo from "./assets/Skogforsk Logotyp_Original_Urskog RGB 750 px.png";

function Layout() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { account, isAdmin, isGuest, login } = useAuth() as {
    account: { name: string; username: string };
    isAdmin: boolean;
    isGuest: boolean;
    login: () => void;
  };

  const handleSignOut = () => {
    instance
      .logoutPopup()
      .then(() => {
        navigate("/");
      })
      .catch((error) => {
        console.error("Logout failed:", error);
      });
  };
  return (
    <SidebarLayout
      navbar={
        <Navbar>
          <NavbarSpacer />
          <NavbarSection className="flex items-center gap-4 justify-center">
            {/* <NavbarItem to="/search" aria-label="Search">
              <MagnifyingGlassIcon />
            </NavbarItem>
            <NavbarItem to="/upload" aria-label="Inbox">
              <InboxIcon />
            </NavbarItem> */}
            <LanguageSwitcher />
            {isGuest ? (
              <NavbarItem onClick={login} aria-label={t("nav.logIn")}>
                <ArrowRightEndOnRectangleIcon className="w-4 h-4" />
              </NavbarItem>
            ) : (
              <Dropdown>
                <DropdownButton as={NavbarItem}>
                  <UserIcon className="w-4 h-4" />
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="bottom end">
                  <DropdownItem to="/profile">
                    <UserIcon />
                    <DropdownLabel>{t("nav.myProfile")}</DropdownLabel>
                  </DropdownItem>
                  {isAdmin && (
                    <DropdownItem to="/admin">
                      <Cog8ToothIcon />
                      <DropdownLabel>{t("nav.admin")}</DropdownLabel>
                    </DropdownItem>
                  )}
                  <DropdownDivider />
                  <DropdownItem onClick={handleSignOut}>
                    <ArrowRightStartOnRectangleIcon />
                    <DropdownLabel>{t("nav.signOut")}</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            )}
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <SidebarHeader>
            <Link to="/" className="flex items-center">
              <img src={skogforskLogo} className="h-16" alt="Skogforsk" />
              <SidebarLabel className="text-base ml-2 font-sans font-semibold">Skogforsklabbet</SidebarLabel>
            </Link>
          </SidebarHeader>

          <SidebarBody>
            <SidebarSection>
              <SidebarItem to="/" current={location.pathname === "/"}>
                <HomeIcon />
                <SidebarLabel>{t("nav.home")}</SidebarLabel>
              </SidebarItem>
              <SidebarItem to="/upload" current={location.pathname === "/upload"}>
                <ArrowUpTrayIcon />
                <SidebarLabel>{t("nav.upload")}</SidebarLabel>
              </SidebarItem>
              <SidebarItem to="/manage-data" current={location.pathname === "/manage-data"}>
                <TableCellsIcon />
                <SidebarLabel>{t("nav.manageData")}</SidebarLabel>
              </SidebarItem>
              {/* <SidebarItem to="/tools" current={location.pathname === "/tools"}>
                <WrenchIcon />
                <SidebarLabel>Tools</SidebarLabel>
              </SidebarItem> */}
              <SidebarItem to="/support" current={location.pathname === "/support"}>
                <QuestionMarkCircleIcon />
                <SidebarLabel>{t("nav.support")}</SidebarLabel>
              </SidebarItem>
              <SidebarItem to="/contact" current={location.pathname === "/contact"}>
                <EnvelopeIcon />
                <SidebarLabel>{t("nav.contact")}</SidebarLabel>
              </SidebarItem>

              <SidebarSpacer />

              <LanguageSwitcher />
            </SidebarSection>
          </SidebarBody>
          <SidebarFooter className="sidebarfooter hidden lg:block">
            {isGuest ? (
              <SidebarItem onClick={login}>
                <ArrowRightEndOnRectangleIcon className="h-8 w-8" />
                <span className="min-w-0">
                  <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">{t("nav.guestUser")}</span>
                  <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">{t("nav.logIn")}</span>
                </span>
              </SidebarItem>
            ) : (
              <Dropdown>
                <DropdownButton as={SidebarItem}>
                  <span className="flex min-w-0 items-center gap-3">
                    <UserIcon className="h-8 w-8" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">{account?.name || "User"}</span>
                      <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">{account?.username || "user@example.com"}</span>
                    </span>
                  </span>
                  <ChevronUpIcon />
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="top start">
                  <DropdownItem to="/profile">
                    <UserIcon className="w-4 h-4" />
                    <DropdownLabel>{t("nav.myProfile")}</DropdownLabel>
                  </DropdownItem>
                  {isAdmin && (
                    <DropdownItem to="/admin">
                      <Cog8ToothIcon />
                      <DropdownLabel>{t("nav.admin")}</DropdownLabel>
                    </DropdownItem>
                  )}

                  <DropdownDivider />
                  <DropdownItem onClick={handleSignOut}>
                    <ArrowRightStartOnRectangleIcon />
                    <DropdownLabel>{t("nav.signOut")}</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            )}
          </SidebarFooter>
        </Sidebar>
      }
    >
      <Outlet />
    </SidebarLayout>
  );
}

export default Layout;
